import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useOrders } from '@t90/api-client';
import { DataTable, Button, Badge, Plus, Search, Input } from '@t90/ui';
import { formatDate } from '@t90/common';
import type { ColumnDef } from '@tanstack/react-table';
import type { Order } from '@t90/common';

const columns: ColumnDef<Order>[] = [
  {
    accessorKey: 'order_number',
    header: 'Order #',
    cell: ({ row }) => (
      <span className="font-medium">{row.getValue('order_number')}</span>
    ),
  },
  {
    accessorKey: 'customer',
    header: 'Customer',
    cell: ({ row }) => {
      const customer = row.original.customer;
      return customer?.customer_name ?? 'Unknown';
    },
  },
  {
    accessorKey: 'order_status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.getValue('order_status') as string;
      return <Badge variant={getStatusVariant(status)}>{status}</Badge>;
    },
  },
  {
    accessorKey: 'priority',
    header: 'Priority',
    cell: ({ row }) => {
      const priority = row.getValue('priority') as string;
      const variant = priority === 'Urgent' ? 'destructive' : priority === 'High' ? 'warning' : 'secondary';
      return <Badge variant={variant}>{priority}</Badge>;
    },
  },
  {
    accessorKey: 'po_number',
    header: 'PO #',
    cell: ({ row }) => row.getValue('po_number') ?? '-',
  },
  {
    accessorKey: 'due_date',
    header: 'Due Date',
    cell: ({ row }) => {
      const date = row.getValue('due_date') as string | null;
      return date ? formatDate(date) : '-';
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => formatDate(row.getValue('created_at')),
  },
];

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'New':
      return 'secondary';
    case 'In Progress':
      return 'default';
    case 'Completed':
      return 'success';
    case 'On Hold':
      return 'warning';
    case 'Cancelled':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function OrdersPage() {
  const navigate = useNavigate();
  const { data: orders, isLoading } = useOrders();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          <p className="text-muted-foreground">
            Manage customer orders and track fulfillment
          </p>
        </div>
        <Button onClick={() => navigate('/orders/new')}>
          <Plus className="mr-2 h-4 w-4" />
          New Order
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={orders ?? []}
        searchKey="order_number"
        searchPlaceholder="Search orders..."
        isLoading={isLoading}
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
        emptyMessage="No orders found. Create your first order to get started."
      />
    </div>
  );
}
